import { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import type { TeamMember, TeamMemberDocument } from '../types';
import type { TeamMemberFilters } from '../components/dashboard/team/TeamMemberFilters';

const DEFAULT_TEAM_MEMBERS: TeamMember[] = [
  {
    id: '1',
    name: 'John Smith',
    role: 'Welder',
    email: 'john.smith@example.com',
    phone: '+1 (555) 123-4567',
    certifications: ['welding-1', 'welding-2', 'safety'],
    qualifications: ['ASME IX', 'AWS D1.1'],
    documents: [
      {
        id: 'doc1',
        fileName: 'welding_certification.pdf',
        type: 'certification',
        uploadDate: '2024-03-15'
      }
    ],
    status: 'active'
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    role: 'QC Inspector',
    email: 'sarah.j@example.com',
    phone: '+1 (555) 234-5678',
    certifications: ['ndt-1', 'ndt-2', 'safety'],
    qualifications: ['API 1169', 'ASNT Level II'],
    documents: [
      {
        id: 'doc2',
        fileName: 'ndt_certification.pdf',
        type: 'certification',
        uploadDate: '2024-03-10'
      }
    ],
    status: 'active'
  },
  {
    id: '3',
    name: 'Michael Chen',
    role: 'Engineer',
    email: 'michael.c@example.com',
    phone: '+1 (555) 345-6789',
    certifications: ['safety'],
    qualifications: ['PE License', 'PMP'],
    documents: [
      {
        id: 'doc3',
        fileName: 'engineering_license.pdf',
        type: 'certification',
        uploadDate: '2024-02-28'
      }
    ],
    status: 'active'
  },
  {
    id: '4',
    name: 'Emily Davis',
    role: 'Customer Representative',
    email: 'emily.d@clientcorp.com',
    phone: '+1 (555) 456-7890',
    certifications: ['safety'],
    qualifications: ['Client Liaison'],
    documents: [],
    status: 'active'
  },
  {
    id: '5',
    name: 'Robert Wilson',
    role: 'Supervisor',
    email: 'robert.w@example.com',
    phone: '+1 (555) 567-8901',
    certifications: ['safety', 'welding-1'],
    qualifications: ['Site Supervision', 'First Aid'],
    documents: [
      {
        id: 'doc4',
        fileName: 'supervisor_certification.pdf',
        type: 'certification',
        uploadDate: '2024-03-01'
      }
    ],
    status: 'active'
  }
];

export const useTeamMembers = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(DEFAULT_TEAM_MEMBERS);
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember>({
    id: '',
    name: '',
    email: '',
    phone: '',
    role: '',
    certifications: [],
    qualifications: [],
    documents: [],
    status: 'active'
  });
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  
  // Add filter state
  const [filters, setFilters] = useState<TeamMemberFilters>({
    search: '',
    role: 'all',
    status: 'all',
  });

  // Filter logic
  const filteredTeamMembers = useMemo(() => {
    return teamMembers.filter(member => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = 
          member.name.toLowerCase().includes(searchTerm) ||
          member.email.toLowerCase().includes(searchTerm);
        if (!matchesSearch) return false;
      }

      // Role filter
      if (filters.role && filters.role !== 'all' && member.role !== filters.role) {
        return false;
      }

      // Status filter
      if (filters.status && filters.status !== 'all' && member.status !== filters.status) {
        return false;
      }

      return true;
    });
  }, [teamMembers, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.role !== 'all' || filters.status !== 'all';
  }, [filters]);

  const handleTeamMemberDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !selectedTeamMember) return;
    
    const newDocuments: TeamMemberDocument[] = Array.from(e.target.files).map(file => ({
      id: crypto.randomUUID(),
      fileName: file.name,
      type: 'other',
      uploadDate: new Date().toISOString()
    }));

    setSelectedTeamMember({
      ...selectedTeamMember,
      documents: [...(selectedTeamMember.documents || []), ...newDocuments]
    });
  };

  const handleSaveTeamMember = () => {
    if (!selectedTeamMember?.name || !selectedTeamMember?.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (selectedTeamMember.id) {
      setTeamMembers(prev =>
        prev.map(member =>
          member.id === selectedTeamMember.id ? selectedTeamMember : member
        )
      );
    } else {
      const newMember: TeamMember = {
        ...selectedTeamMember,
        id: crypto.randomUUID(),
        status: 'active'
      };
      setTeamMembers(prev => [...prev, newMember]);
    } 

    setSelectedTeamMember({
      id: '',
      name: '',
      email: '',
      phone: '',
      role: '',
      certifications: [],
      qualifications: [],
      documents: [],
      status: 'active'
    });
    setIsAddingTeamMember(false);
    toast.success(`Team member ${selectedTeamMember.id ? 'updated' : 'added'} successfully`);
  };

  const handleEditTeamMember = (member: TeamMember) => {
    setSelectedTeamMember(member);
    setIsAddingTeamMember(true);
  };

  const handleViewTeamMemberDocuments = (member: TeamMember) => {
    setSelectedTeamMember(member);
    // Additional logic for viewing documents can be added here
  };

  const handleAddNewTeamMember = () => {
    setSelectedTeamMember({
      id: '',
      name: '',
      role: '',
      email: '',
      phone: '',
      certifications: [],
      qualifications: [],
      documents: [],
      status: 'active'
    });
    setIsAddingTeamMember(true);
  };

  return {
    teamMembers,
    filteredTeamMembers,
    selectedTeamMember,
    isAddingTeamMember,
    filters,
    hasActiveFilters,
    setSelectedTeamMember,
    setIsAddingTeamMember,
    setFilters,
    handleTeamMemberDocumentUpload,
    handleSaveTeamMember,
    handleEditTeamMember,
    handleViewTeamMemberDocuments,
    handleAddNewTeamMember
  };
}; 